// The pieces every screen is built from.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { errorText, isSignedOut } from "./api.ts";
import { cap } from "./format.ts";
import { Icon } from "./icons.tsx";
import { humanTag, lichessTheme } from "./kinds.ts";

export function ScreenHead({ title, subtitle, actions, many }: { title: string; subtitle?: ReactNode; actions?: ReactNode; many?: boolean }) {
  return (
    <div className={"screen-head" + (many ? " many" : "")}>
      <div>
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}

export function Loading({ text }: { text: string }) {
  return (
    <div className="loading-row">
      <span className="spinner" />
      {text}
    </div>
  );
}

export function Notice({ children, kind }: { children: ReactNode; kind?: "error" | "accent" }) {
  return <div className={"notice" + (kind ? " " + kind : "")}>{children}</div>;
}

export interface SegOption<T extends string> {
  value: T;
  label: ReactNode;
  title?: string;
}

/** A row of buttons, one of them pressed. */
export function Seg<T extends string>({ options, value, onChange, className }: { options: SegOption<T>[]; value: T; onChange: (v: T) => void; className?: string }) {
  return (
    <div className={"seg" + (className ? " " + className : "")} role="group">
      {options.map((opt) => (
        <button key={opt.value} type="button" aria-pressed={opt.value === value} title={opt.title} onClick={() => onChange(opt.value)}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/** Lichess's own trainer, filtered to one theme: a way out of the app. */
export function LichessLink({ tag, children }: { tag: string; children?: ReactNode }) {
  const theme = lichessTheme(tag);
  const nearest = theme !== tag;
  return (
    <a
      className="btn btn-sm"
      href={`https://lichess.org/training/${theme}`}
      target="_blank"
      rel="noopener"
      title={
        nearest
          ? `Lichess has no theme for ${humanTag(tag)}; ${humanTag(theme)} is the nearest it does have.`
          : `Lichess's own puzzles, filtered to ${humanTag(tag)}.`
      }
    >
      {children ?? (
        <>
          <Icon name="ext" />
          More on Lichess
        </>
      )}
    </a>
  );
}

export const SideMark = ({ color }: { color: string }) => (
  <span className={"res side-mark " + color} title={cap(color)}>
    {color === "black" ? "♚" : "♔"}
  </span>
);

export interface MenuItem {
  label: string;
  onClick: () => void;
  title?: string;
  danger?: boolean;
}

/** What a screen can do but is not pressed again and again lives behind one
 *  button, marked ⋯. A press anywhere else puts it away. */
export function Menu({ items, className }: { items: (MenuItem | null)[]; className?: string }) {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) ref.current.removeAttribute("open");
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);
  return (
    <details className={"menu" + (className ? " " + className : "")} ref={ref}>
      <summary className="btn" aria-label="More">
        ⋯
      </summary>
      <div className="menu-list">
        {items.map((item) =>
          item ? (
            <button
              key={item.label}
              type="button"
              className={"menu-item" + (item.danger ? " danger" : "")}
              title={item.title}
              onClick={() => {
                ref.current?.removeAttribute("open");
                item.onClick();
              }}
            >
              {item.label}
            </button>
          ) : null
        )}
      </div>
    </details>
  );
}

export type Loaded<T> = { state: "loading" } | { state: "error"; error: string } | { state: "ready"; data: T };

/** A request made when a screen opens, and again on `reload`. Signing out is
 *  the gate's business, not the screen's, so it leaves the screen loading. */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []): [Loaded<T>, () => void, (data: T) => void] {
  const [value, setValue] = useState<Loaded<T>>({ state: "loading" });
  const seq = useRef(0);
  const run = useCallback(() => {
    const mine = ++seq.current;
    setValue((v) => (v.state === "ready" ? v : { state: "loading" }));
    load().then(
      (data) => mine === seq.current && setValue({ state: "ready", data }),
      (err) => {
        if (mine !== seq.current || isSignedOut(err)) return;
        setValue({ state: "error", error: errorText(err) });
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(run, [run]);
  return [value, run, (data: T) => setValue({ state: "ready", data })];
}

/** What a loading screen shows until it has something: the spinner, or
 *  the reason it has nothing. */
export function Pending({ value, text }: { value: Loaded<unknown>; text: string }) {
  if (value.state === "error") return <Notice kind="error">{value.error}</Notice>;
  return <Loading text={text} />;
}
